import { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell,
  Pagination, Stack, Menu, MenuItem, IconButton,
  InputAdornment, OutlinedInput,
} from '@mui/material';
import {
  ContentCopy, Check, Warning,
  MoreVert, Search, ArrowUpward, ArrowDownward, UnfoldMore,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuthTenant } from 'store/auth';
import { getScannerAccounts, getScannerResources } from 'services/scanner-service';
import { Main as Container } from 'Components/Main';
import { Select, PageHeader } from 'Components/ui';

// ── Region flags ──────────────────────────────────────────────────────────────

const REGION_FLAGS = {
  'us-east-1': '🇺🇸', 'us-east-2': '🇺🇸', 'us-west-1': '🇺🇸', 'us-west-2': '🇺🇸',
  'us-gov-west-1': '🇺🇸', 'us-gov-east-1': '🇺🇸',
  'eu-west-1': '🇮🇪', 'eu-west-2': '🇬🇧', 'eu-west-3': '🇫🇷',
  'eu-central-1': '🇩🇪', 'eu-central-2': '🇨🇭', 'eu-north-1': '🇸🇪',
  'eu-south-1': '🇮🇹', 'eu-south-2': '🇪🇸',
  'ap-southeast-1': '🇸🇬', 'ap-southeast-2': '🇦🇺', 'ap-southeast-3': '🇮🇩',
  'ap-northeast-1': '🇯🇵', 'ap-northeast-2': '🇰🇷', 'ap-northeast-3': '🇯🇵',
  'ap-south-1': '🇮🇳', 'ap-south-2': '🇮🇳',
  'ap-east-1': '🇭🇰', 'ap-central-1': '🇨🇦',
  'sa-east-1': '🇧🇷',
  'ca-central-1': '🇨🇦', 'ca-west-1': '🇨🇦',
  'me-south-1': '🇧🇭', 'me-central-1': '🇦🇪',
  'af-south-1': '🇿🇦',
  'il-central-1': '🇮🇱',
  'global': '🌐',
};

// ── Provider badge ────────────────────────────────────────────────────────────

const PROVIDER_STYLE = {
  aws:   { label: 'AWS',   bg: 'color-mix(in srgb, #FF9900 18%, transparent)',   color: '#FF9900', border: 'color-mix(in srgb, #FF9900 40%, transparent)' },
  azure: { label: 'Azure', bg: 'color-mix(in srgb, #0078D4 18%, transparent)', color: '#0078D4', border: 'color-mix(in srgb, #0078D4 40%, transparent)' },
  gcp:   { label: 'GCP',  bg: 'color-mix(in srgb, #4285F4 18%, transparent)',  color: '#4285F4', border: 'color-mix(in srgb, #4285F4 40%, transparent)' },
};

const ProviderBadge = ({ provider }) => {
  const s = PROVIDER_STYLE[provider?.toLowerCase()] ?? { label: provider ?? '?', bg: 'var(--vo-mist)', color: 'var(--vo-slate)', border: 'var(--vo-line)' };
  return (
    <span style={{
      display: 'inline-block', fontSize: 10, fontWeight: 700,
      padding: '2px 6px', borderRadius: 4,
      backgroundColor: s.bg, color: s.color, border: `1px solid ${s.border}`,
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>{s.label}</span>
  );
};

// ── Copy button ───────────────────────────────────────────────────────────────

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handle = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handle} title="Copiar" style={{
      background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
      color: copied ? 'var(--vo-success)' : 'var(--vo-steel)', display: 'inline-flex', alignItems: 'center',
      transition: 'color 0.15s', flexShrink: 0,
    }}>
      {copied
        ? <Check style={{ fontSize: 11 }} />
        : <ContentCopy style={{ fontSize: 11 }} />}
    </button>
  );
};

// ── Fail badge ────────────────────────────────────────────────────────────────

const FailBadge = ({ count }) => {
  if (!count) return <Typography sx={{ color: 'var(--vo-steel)', fontSize: 12 }}>—</Typography>;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Warning sx={{ fontSize: 14, color: 'var(--vo-danger)' }} />
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--vo-danger)' }}>
        {count}
      </Typography>
    </Box>
  );
};

// ── Sort header cell ──────────────────────────────────────────────────────────

const SortableHeader = ({ label, field, sort, onSort }) => {
  const active = sort.field === field;
  const Icon = active ? (sort.dir === 'asc' ? ArrowUpward : ArrowDownward) : UnfoldMore;
  return (
    <TableCell
      sx={{ ...TH, cursor: 'pointer', userSelect: 'none', '&:hover': { color: 'var(--vo-ink)' } }}
      onClick={() => onSort(field)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {label}
        <Icon sx={{ fontSize: 13, color: active ? 'var(--vo-celeste)' : 'var(--vo-steel)' }} />
      </Box>
    </TableCell>
  );
};

// ── Actions menu ──────────────────────────────────────────────────────────────

const ActionsMenu = () => {
  const { t } = useTranslation('scanner');
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}>
        <MoreVert sx={{ fontSize: 16, color: 'var(--vo-steel)' }} />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ elevation: 2, sx: { minWidth: 160, borderRadius: 1 } }}
      >
        <MenuItem
          sx={{ fontSize: 13 }}
          onClick={() => { setAnchor(null); navigate('/scanner/findings'); }}
        >
          {t('scanner:resources.viewFindings')}
        </MenuItem>
      </Menu>
    </>
  );
};

// ── Resource row ──────────────────────────────────────────────────────────────

const ResourceRow = ({ resource: r }) => {
  const arn = r.uid || '';
  const arnShort = arn.length > 48 ? `${arn.slice(0, 46)}…` : arn;
  const regionFlag = REGION_FLAGS[r.region] ?? REGION_FLAGS['global'];
  const groups = r.groups ?? [];

  return (
    <TableRow hover sx={{ '&:last-child td': { borderBottom: 0 }, cursor: 'default' }}>

      {/* Name + ARN */}
      <TableCell sx={{ py: 1.5, minWidth: 240 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--vo-ink)', lineHeight: 1.3 }}>
          {r.name || r.uid}
        </Typography>
        {arn && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}>
            <Typography sx={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--vo-steel)', lineHeight: 1.4 }} title={arn}>
              {arnShort}
            </Typography>
            <CopyButton text={arn} />
          </Box>
        )}
      </TableCell>

      {/* Provider: badge + account name + account UID */}
      <TableCell sx={{ py: 1.5, minWidth: 180 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.3 }}>
          <ProviderBadge provider={r.account_provider ?? 'aws'} />
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'var(--vo-ink)' }}>
            {r.account_name || '—'}
          </Typography>
        </Box>
        {r.account_uid && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
            <Typography sx={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--vo-steel)' }}>
              {r.account_uid}
            </Typography>
            <CopyButton text={r.account_uid} />
          </Box>
        )}
      </TableCell>

      {/* Failed Findings */}
      <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
        <FailBadge count={r.failed_findings_count} />
      </TableCell>

      {/* Group */}
      <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
        <Typography sx={{ fontSize: 12, color: 'var(--vo-ink-2)' }}>
          {groups.length > 0 ? groups.join(', ') : '—'}
        </Typography>
      </TableCell>

      {/* Type */}
      <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
        <Typography sx={{ fontSize: 12, color: 'var(--vo-ink-2)', fontFamily: 'monospace' }}>
          {r.type || '—'}
        </Typography>
      </TableCell>

      {/* Region */}
      <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <span style={{ fontSize: 14, lineHeight: 1 }}>{regionFlag}</span>
          <Typography sx={{ fontSize: 12, color: 'var(--vo-ink-2)', fontFamily: 'monospace' }}>
            {r.region || 'global'}
          </Typography>
        </Box>
      </TableCell>

      {/* Service */}
      <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
        <Typography sx={{ fontSize: 12, color: 'var(--vo-ink-2)' }}>
          {r.service || '—'}
        </Typography>
      </TableCell>

      {/* Actions */}
      <TableCell sx={{ py: 1.5, pr: 1, width: 48 }}>
        <ActionsMenu />
      </TableCell>

    </TableRow>
  );
};

const TH = {
  fontWeight: 600, fontSize: 11, color: 'var(--vo-slate)',
  textTransform: 'uppercase', letterSpacing: 0.4, py: 1.5,
};

// ── Main component ────────────────────────────────────────────────────────────

export const ScannerResources = () => {
  const { t } = useTranslation('scanner');
  const selectedTenant = useSelector(useAuthTenant);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [sort, setSort] = useState({ field: 'failed_findings_count', dir: 'desc' });

  const { data: accountsData } = useQuery(
    ['scannerAccounts', selectedTenant.slug],
    () => getScannerAccounts(selectedTenant.slug),
    { enabled: !!selectedTenant.slug }
  );

  const { data, isLoading, isError, error } = useQuery(
    ['scannerResources', selectedTenant.slug, page],
    () => getScannerResources(selectedTenant.slug, page, 20),
    { enabled: !!selectedTenant.slug, keepPreviousData: true }
  );

  const accounts = accountsData?.data ?? [];
  const pagination = data?.data?.pagination ?? {};
  const totalPages = pagination.pages ?? 1;
  const totalCount = pagination.count ?? 0;

  const handleSort = (field) => {
    setSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  const resources = useMemo(() => {
    const rawResources = data?.data?.resources ?? [];
    let filtered = rawResources;

    if (accountFilter) {
      filtered = filtered.filter(r => r.account_uid === accountFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.uid || '').toLowerCase().includes(q) ||
        (r.type || '').toLowerCase().includes(q)
      );
    }

    const { field, dir } = sort;
    filtered = [...filtered].sort((a, b) => {
      let av = a[field] ?? 0;
      let bv = b[field] ?? 0;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [data, accountFilter, search, sort]);

  const accountOptions = [
    { value: '', label: t('scanner:resources.allAccounts') },
    ...accounts.map(a => ({ value: a.uid, label: a.name })),
  ];

  return (
    <Container>
      <PageHeader
        title={t('scanner:resources.title')}
        segments={[
          { label: t('common:breadcrumbs.analysis') },
          { label: t('scanner:dashboard.title'), route: '/scanner/dashboard' },
          { label: t('scanner:resources.title') },
        ]}
      />
      <Box sx={{ mt: 3, pb: 6 }}>

        {/* Filter bar */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {accounts.length > 0 && (
            <Select
              value={accountFilter}
              onChange={(val) => { setAccountFilter(val); setPage(1); }}
              style={{ minWidth: 180 }}
              options={accountOptions}
            />
          )}

          {/* Search */}
          <OutlinedInput
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder={t('scanner:resources.searchPlaceholder')}
            size="small"
            sx={{
              fontSize: 13, height: 36, flex: '1 1 220px', maxWidth: 380,
              backgroundColor: 'var(--vo-white)',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--vo-line)' },
            }}
            startAdornment={
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16, color: 'var(--vo-steel)' }} />
              </InputAdornment>
            }
          />

          {/* Total entries */}
          {totalCount > 0 && (
            <Typography sx={{ fontSize: 13, color: 'var(--vo-slate)', ml: 'auto', whiteSpace: 'nowrap' }}>
              {t('scanner:resources.totalEntries', { count: totalCount.toLocaleString() })}
            </Typography>
          )}
        </Box>

        {/* Loading */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {/* Error */}
        {isError && (
          <Box sx={{ p: 3, border: '1px solid var(--vo-danger-15)', borderRadius: 2, backgroundColor: 'var(--vo-danger-10)' }}>
            <Typography sx={{ fontSize: 13, color: 'var(--vo-danger)', fontFamily: 'monospace' }}>
              {error?.response?.data?.message || error?.message || 'Erro ao carregar recursos.'}
            </Typography>
          </Box>
        )}

        {/* Empty */}
        {!isLoading && !isError && resources.length === 0 && (
          <Box sx={{ p: 5, border: '1px solid var(--vo-line)', borderRadius: 2, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 600, color: 'var(--vo-ink)', mb: 0.5 }}>
              {t('scanner:resources.emptyTitle')}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'var(--vo-slate)' }}>
              {t('scanner:resources.emptyDesc')}
            </Typography>
          </Box>
        )}

        {/* Table */}
        {resources.length > 0 && (
          <Box sx={{ border: '1px solid var(--vo-line)', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 960 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'var(--vo-mist)' }}>
                    <TableCell sx={TH}>{t('scanner:resources.colResource')}</TableCell>
                    <TableCell sx={TH}>{t('scanner:resources.colProvider')}</TableCell>
                    <SortableHeader
                      label={t('scanner:resources.colFails')}
                      field="failed_findings_count"
                      sort={sort}
                      onSort={handleSort}
                    />
                    <TableCell sx={TH}>{t('scanner:resources.colGroup')}</TableCell>
                    <SortableHeader
                      label={t('scanner:resources.colType')}
                      field="type"
                      sort={sort}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label={t('scanner:resources.colRegion')}
                      field="region"
                      sort={sort}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label={t('scanner:resources.colService')}
                      field="service"
                      sort={sort}
                      onSort={handleSort}
                    />
                    <TableCell sx={{ ...TH, width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resources.map((r) => (
                    <ResourceRow key={r.id ?? r.uid} resource={r} />
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Stack alignItems="center" sx={{ mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
              size="small"
            />
          </Stack>
        )}

      </Box>
    </Container>
  );
};
